import { emit } from './store-events.js';

const DESKTOP_MEDIA_QUERY = '(min-width: 50.9375rem)';

class StoreDialogElement extends HTMLElement {
  connectedCallback() { try { this.#init(); } catch (_) {} }
  disconnectedCallback() {
    try {
      this.#cancelBusy();
      if (this.__onKeydown) document.removeEventListener('keydown', this.__onKeydown, true);
      if (this.__onTrapTab) document.removeEventListener('keydown', this.__onTrapTab, true);
      if (this.__onClick) this.removeEventListener('click', this.__onClick, true);
    } catch (_) {}
  }
  #init() {
    try { this.setAttribute('data-dialog-root',''); } catch (_) {}
    try { if (!this.hasAttribute('role')) this.setAttribute('role','dialog'); } catch (_) {}
    try { if (!this.hasAttribute('aria-modal')) this.setAttribute('aria-modal','true'); } catch (_) {}
    try { if (!this.hasAttribute('data-align')) this.setAttribute('data-align','center'); } catch (_) {}

    if (typeof this.__hadHiddenInitial === 'undefined') {
      try { this.__hadHiddenInitial = this.classList.contains('hidden'); } catch (_) { this.__hadHiddenInitial = false; }
    }

    this.__onClick = (ev) => {
      try {
        // Close when clicking explicit close controls or the overlay, not the panel
        const overlay = ev.target && (ev.target.matches && ev.target.matches('[data-modal-overlay]'));
        const closeEl = ev.target && ev.target.closest && ev.target.closest('[data-modal-close]');
        if (!overlay && !closeEl) return;
        ev.preventDefault();
        ev.stopPropagation();
        this.close();
      } catch (_) {}
    };
    this.addEventListener('click', this.__onClick, true);

    this.__onKeydown = (ev) => { try { if (ev.key === 'Escape' && this.isOpen()) { ev.preventDefault(); this.close(); } } catch (_) {} };
    document.addEventListener('keydown', this.__onKeydown, true);
  }
  isOpen() { return this.hasAttribute('data-open'); }
  #transitionDurMs() {
    try {
      const cs = getComputedStyle(this);
      const durStr = (cs.getPropertyValue('--tr-duration') || '').trim();
      if (durStr.endsWith('ms')) return Math.max(0, parseFloat(durStr));
      if (durStr.endsWith('s')) return Math.max(0, Math.round(parseFloat(durStr) * 1000));
    } catch (_) {}
    return 250;
  }
  #setBusyFor(ms) {
    try {
      this.__transitioning = true;
      if (this.__busyTimerId) { clearTimeout(this.__busyTimerId); this.__busyTimerId = null; }
      this.__busyTimerId = setTimeout(() => { this.__busyTimerId = null; this.__transitioning = false; }, Math.max(0, ms || 0));
    } catch (_) {}
  }
  #cancelBusy() {
    try {
      this.__transitioning = false;
      if (this.__busyTimerId) { clearTimeout(this.__busyTimerId); this.__busyTimerId = null; }
      if (this.__openRafId) { (typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame : clearTimeout)(this.__openRafId); this.__openRafId = null; }
      if (this.__hideTimerId) { clearTimeout(this.__hideTimerId); this.__hideTimerId = null; }
    } catch (_) {}
  }
  #isDesktopViewport() {
    try { return typeof window !== 'undefined' && window.matchMedia && window.matchMedia(DESKTOP_MEDIA_QUERY).matches; } catch (_) { return false; }
  }
  #scrollLockMode() {
    if (!this.hasAttribute('data-scroll-lock')) return '';
    try {
      const raw = (this.getAttribute('data-scroll-lock') || '').trim().toLowerCase();
      if (!raw) return 'always';
      if (['true','1','yes','always','lock'].includes(raw)) return 'always';
      if (['false','0','no','none','off'].includes(raw)) return '';
      if (['mobile','sm','base'].includes(raw)) return 'mobile';
      if (['desktop','lg'].includes(raw)) return 'desktop';
      return raw;
    } catch (_) {
      return 'always';
    }
  }
  #shouldLockScroll() {
    const mode = this.#scrollLockMode();
    if (!mode) return false;
    if (mode === 'mobile') return !this.#isDesktopViewport();
    if (mode === 'desktop') return this.#isDesktopViewport();
    return mode === 'always';
  }
  open() {
    try {
      // Cancel any pending close operations first (allows re-open during close transition)
      this.#cancelBusy();
      if (this.isOpen()) return;
      // Guard: hamburger menu should not open on desktop (>= lg)
      try {
        if (this.id === 'menu' && this.#isDesktopViewport()) {
          return;
        }
      } catch (_) {}
      this.#setBusyFor(this.#transitionDurMs());
      // If root was hidden initially, unhide first and force reflow to enable transitions
      try { this.classList.remove('hidden'); } catch (_) {}
      try { void this.getBoundingClientRect(); } catch (_) {}
      // Track rAF so close() can cancel it (prevents zombie data-open)
      if (this.__openRafId) { try { (typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame : clearTimeout)(this.__openRafId); } catch (_) {} }
      const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb) => setTimeout(cb, 0);
      this.__openRafId = raf(() => { this.__openRafId = null; try { this.setAttribute('data-open',''); } catch (_) {} });
      const shouldLockScroll = this.#shouldLockScroll();
      if (shouldLockScroll) {
        try { document.body.classList.add('overflow-hidden'); } catch (_) {}
        try { document.documentElement && document.documentElement.classList && document.documentElement.classList.add('overflow-hidden'); } catch (_) {}
        try { this.setAttribute('data-scroll-locked','true'); } catch (_) {}
        this.__didLockScroll = true;
      } else {
        try { this.removeAttribute('data-scroll-locked'); } catch (_) {}
        this.__didLockScroll = false;
      }
      // focus trap
      this.__previousActive = document.activeElement;
      this.__focusables = this.#focusables();
      try { (this.__focusables[0] || this).focus({ preventScroll: true }); } catch (_) {}
      this.__onTrapTab = (ev) => {
        try {
          if (ev.key !== 'Tab' || !this.isOpen()) return;
          const list = this.__focusables || this.#focusables();
          if (!list.length) return;
          const first = list[0];
          const last = list[list.length - 1];
          if (ev.shiftKey) { if (document.activeElement === first || !this.contains(document.activeElement)) { ev.preventDefault(); last.focus({ preventScroll:true }); } }
          else { if (document.activeElement === last) { ev.preventDefault(); first.focus({ preventScroll:true }); } }
        } catch (_) {}
      };
      document.addEventListener('keydown', this.__onTrapTab, true);
      emit('store:dialog:open', { id: this.id, align: this.getAttribute('data-align') }, this);
    } catch (_) {}
  }
  close() {
    try {
      // Cancel any pending open rAF (prevents zombie data-open after close)
      this.#cancelBusy();
      if (!this.isOpen()) return;
      this.#setBusyFor(this.#transitionDurMs());
      this.removeAttribute('data-open');
      try { if (this.__onTrapTab) document.removeEventListener('keydown', this.__onTrapTab, true); } catch (_) {}
      try { if (this.__previousActive && this.__previousActive.focus) this.__previousActive.focus({ preventScroll: true }); } catch (_) {}
      // Delay re-adding hidden (if present initially) until after transition completes
      try {
        let ms = this.#transitionDurMs();
        // Instant close only when explicitly requested via attribute
        const instant = this.hasAttribute('data-instant-close');
        const delay = instant ? 0 : (isFinite(ms) && ms > 0 ? ms : 250);
        this.__hideTimerId = setTimeout(() => { this.__hideTimerId = null; try { this.classList.add('hidden'); } catch (_) {} }, delay);
      } catch (_) { if (this.__hadHiddenInitial) { try { this.classList.add('hidden'); } catch (__) {} } }
      const hadScrollLock = this.hasAttribute('data-scroll-locked');
      if (hadScrollLock) {
        try {
          // Only remove scroll lock if no other scroll-locking dialogs are still open
          const anyOtherLockedOpen = Array.from(document.querySelectorAll('store-dialog[data-open][data-scroll-locked="true"]')).some((el) => el !== this);
          if (!anyOtherLockedOpen) {
            try { document.body.classList.remove('overflow-hidden'); } catch (_) {}
            try { document.documentElement && document.documentElement.classList && document.documentElement.classList.remove('overflow-hidden'); } catch (_) {}
          }
        } catch (_) {}
      }
      try { this.removeAttribute('data-scroll-locked'); } catch (_) {}
      this.__didLockScroll = false;
      emit('store:dialog:close', { id: this.id, align: this.getAttribute('data-align') }, this);
    } catch (_) {}
  }
  toggle() { this.isOpen() ? this.close() : this.open(); }
  #focusables() {
    try {
      const sel = ['a[href]','button:not([disabled])','textarea:not([disabled])','input:not([disabled])','select:not([disabled])','[tabindex]:not([tabindex="-1"])'];
      return Array.from(this.querySelectorAll(sel.join(','))).filter((el) => el.offsetParent !== null || el === document.activeElement);
    } catch (_) { return []; }
  }
}

try { if (!customElements.get('store-dialog')) customElements.define('store-dialog', StoreDialogElement); } catch (_) {}

